import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, of, timeout } from 'rxjs';
import { catchError, delay, map, switchMap } from 'rxjs/operators';
import { Experience } from 'src/app/services/experience.service';

export type SmartSearchSuggestionType =
  | 'location'
  | 'category'
  | 'location_category'
  | 'page'
  | 'experience';

export interface SmartSearchSuggestion {
  id: string;
  type: SmartSearchSuggestionType;
  title: string;
  /** Short one-liner shown under the title */
  subtitle?: string;
  url: string;
  confidence: number;
  badge?: string;
}

export interface SmartSearchResult {
  query: string;
  primary: SmartSearchSuggestion | null;
  suggestions: SmartSearchSuggestion[];
  /** Shown when nothing matched — how to use smart search */
  guidance?: string[];
  experiencesLabel?: string;
}

interface CatalogItem {
  id: string;
  type: 'location' | 'category' | 'page';
  name: string;
  slug: string;
  aliases: string[];
  parentSlug?: string;
  countrySlug?: string;
  tagline?: string;
  defaultLocSlug?: string;
}

interface ScoredHit {
  item: CatalogItem;
  score: number;
  matchedAlias?: string;
}

interface ExpCacheEntry {
  at: number;
  items: SmartSearchSuggestion[];
}

/**
 * Header "Search anything here" — uses the same search-experience-autocomplete
 * API as the homepage hero so results stay relevant (e.g. “Helico” → helicopters).
 */
@Injectable({
  providedIn: 'root'
})
export class SmartSearchService {
  readonly enabled = true;

  private readonly useAanyaApi = false;
  private readonly aanyaSmartSearchPath = '/api/smart-search';
  private readonly searchUxDelayMs = 180;
  /** Hard caps — protect browser / API / DB */
  private readonly maxExperiences = 5;
  private readonly maxSuggestions = 12;
  private readonly experienceApiTimeoutMs = 3500;
  private readonly experienceCacheTtlMs = 60_000;
  private readonly experienceCache = new Map<string, ExpCacheEntry>();
  /** Match homepage hero: API typically becomes useful from 4 characters */
  private readonly minQueryLength = 4;

  private catalog: CatalogItem[] = [];
  private defaultLocationSlug = 'united-states';
  private readonly isBrowser: boolean;

  private readonly stopWords = new Set([
    'in', 'the', 'a', 'an', 'of', 'for', 'near', 'around', 'to', 'and', 'or', 'at', 'on', 'with', 'from', 'by',
    'show', 'find', 'list', 'get', 'see', 'some', 'any', 'all', 'best', 'top'
  ]);

  /** Fluff words — never drive category/location matching by themselves */
  private readonly noiseTitleWords = new Set([
    'experience', 'experiences', 'activity', 'activities', 'thing', 'things',
    'place', 'places', 'idea', 'ideas', 'gift', 'gifts', 'option', 'options',
    'something', 'anything', 'somewhere', 'trip', 'trips', 'vacation', 'holiday',
    'ride', 'rides', 'tour', 'tours', 'adventure', 'adventures', 'class', 'classes',
    'lesson', 'lessons', 'session', 'sessions', 'package', 'packages', 'day', 'days',
    'night', 'nights', 'fun', 'cool', 'awesome', 'great', 'amazing'
  ]);

  /** Extra place cues used to verify an experience belongs to a location */
  private readonly locationGeoHints: Record<string, string[]> = {
    florida: [
      'florida', 'fl', 'miami', 'orlando', 'tampa', 'jacksonville', 'naples',
      'key west', 'fort lauderdale', 'sarasota', 'pensacola', 'st petersburg',
      'saint petersburg', 'clearwater', 'gainesville', 'tallahassee', 'daytona',
      'west palm', 'boca raton', 'kissimmee', 'cape canaveral', 'florida keys'
    ],
    'united-states': [
      'united states', 'usa', 'u s', 'america', 'alabama', 'alaska', 'arizona',
      'california', 'colorado', 'florida', 'georgia', 'hawaii', 'illinois',
      'louisiana', 'nevada', 'new york', 'texas', 'washington', 'miami', 'orlando',
      'los angeles', 'las vegas', 'chicago', 'boston', 'seattle', 'denver'
    ],
    mexico: [
      'mexico', 'méxico', 'cancun', 'cancún', 'tulum', 'cabo', 'los cabos',
      'playa del carmen', 'riviera maya', 'mexico city', 'guadalajara', 'puerto vallarta',
      'cozumel', 'merida', 'oaxaca'
    ],
    'new-york': [
      'new york', 'nyc', 'manhattan', 'brooklyn', 'queens', 'bronx', 'ny '
    ],
  };

  private readonly tourKeywords = [
    'city tour',
    'city tours',
    'citytour',
    'sightseeing',
    'guided tour',
    'guided tours',
    'walking tour',
    'walking tours',
    'tour',
    'tours',
  ];

  private readonly emptyGuidance = [
    'Type at least 4 letters — e.g. “Helico” for helicopter rides.',
    'Try a destination — e.g. “Florida” or “Mexico”.',
    'Try an experience type — e.g. “helicopter” or “spa”.',
    'Pick a suggestion to open that page, or browse Locations from the menu.',
  ];

  private readonly staticPages: CatalogItem[] = [
    {
      id: 'page-gift-ideas',
      type: 'page',
      name: 'Gifts',
      slug: 'gifts',
      aliases: ['gift ideas', 'gifts', 'gift'],
      tagline: 'Curated experience gifts for every occasion',
    },
    {
      id: 'page-at-home',
      type: 'page',
      name: 'At-Home Experiences',
      slug: 'at-home-experiences',
      aliases: ['at home', 'athome', 'home experiences'],
      tagline: 'Memorable experiences you can enjoy from home',
    },
    {
      id: 'page-locations',
      type: 'page',
      name: 'Locations',
      slug: 'locations',
      aliases: ['all locations', 'browse locations'],
      tagline: 'Browse destinations around the world',
    },
  ];

  private readonly aliasBoosts: Record<string, string[]> = {
    'united-states': ['us', 'usa', 'u.s.', 'u.s.a', 'america', 'united states'],
    'united-kingdom': ['uk', 'u.k.', 'britain', 'england', 'great britain'],
    mexico: ['mx', 'méxico'],
    florida: ['fl', 'florida state'],
    'new-york': ['nyc', 'new york city', 'ny', 'manhattan'],
    'new-york-city': ['nyc', 'new york', 'ny', 'manhattan'],
    'animal-experiences': ['animal', 'animals', 'animal experience', 'zoo', 'wildlife'],
    'animal-experience': ['animal', 'animals', 'animal experience', 'zoo', 'wildlife'],
    helicopter: ['heli', 'chopper', 'helicopter ride', 'helicopter rides', 'helicopter'],
    'helicopter-rides': ['heli', 'chopper', 'helicopter ride', 'helicopter rides'],
    'hot-air-balloon': ['balloon', 'hot air balloon', 'balloon ride'],
    spa: ['spa day', 'massage', 'wellness'],
    food: ['dining', 'restaurant', 'foodie', 'culinary'],
    adventure: ['adventures', 'adrenaline', 'outdoor'],
    tour: ['city tour', 'city tours', 'sightseeing', 'guided tour', 'walking tour', 'tours'],
    tours: ['city tour', 'city tours', 'sightseeing', 'guided tour', 'walking tour', 'tour'],
    'city-tours': ['city tour', 'city tours', 'sightseeing', 'guided tour', 'walking tour'],
    'city-tour': ['city tour', 'city tours', 'sightseeing', 'guided tour', 'walking tour'],
  };

  constructor(
    private experienceService: Experience,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.catalog = [...this.staticPages];
  }

  registerCatalog(input: {
    locations?: any[];
    categories?: any[];
    defaultLocationSlug?: string;
  }): void {
    if (input.defaultLocationSlug) {
      this.defaultLocationSlug = input.defaultLocationSlug;
    }

    const items: CatalogItem[] = [...this.staticPages];

    if (Array.isArray(input.locations)) {
      for (const loc of input.locations) {
        this.flattenLocationTree(items, loc, undefined, loc?.slug);
      }
    }

    if (Array.isArray(input.categories)) {
      for (const cat of input.categories) {
        this.pushCategory(items, cat);
        if (Array.isArray(cat?.child_categories)) {
          for (const child of cat.child_categories) {
            this.pushCategory(items, child);
          }
        }
      }
    }

    this.catalog = items;
  }

  search(query: string): Observable<SmartSearchResult> {
    const trimmed = (query || '').trim();
    // Same threshold as homepage hero (search-experience-autocomplete)
    if (!trimmed || trimmed.length < this.minQueryLength) {
      return of({ query: trimmed, primary: null, suggestions: [], guidance: [] });
    }

    if (!this.isBrowser) {
      return of({ query: trimmed, primary: null, suggestions: [], guidance: [] });
    }

    const token =
      (typeof localStorage !== 'undefined' && localStorage.getItem('login_token')) || '';

    return of(null).pipe(
      delay(this.searchUxDelayMs),
      switchMap(() =>
        this.experienceService.searchExperienceAutocomplete({ search: trimmed }, token).pipe(
          timeout(this.experienceApiTimeoutMs),
          map((res: any) => this.mapAutocompleteApiResult(trimmed, res)),
          catchError(() =>
            of({
              query: trimmed,
              primary: null,
              suggestions: [] as SmartSearchSuggestion[],
              guidance: [...this.emptyGuidance],
            })
          )
        )
      ),
      map((result) => this.finalizeResult(result)),
      catchError(() =>
        of(
          this.finalizeResult({
            query: trimmed,
            primary: null,
            suggestions: [],
            guidance: [...this.emptyGuidance],
          })
        )
      )
    );
  }

  /**
   * Map homepage `search-experience-autocomplete` rows into header suggestions.
   * Keeps the same relevance the hero search already gets from the API.
   */
  private mapAutocompleteApiResult(query: string, res: any): SmartSearchResult {
    if (!res || res.response !== 'success' || !Array.isArray(res.data)) {
      return { query, primary: null, suggestions: [], guidance: [...this.emptyGuidance] };
    }

    const suggestions: SmartSearchSuggestion[] = [];
    for (const row of res.data) {
      const mapped = this.mapAutocompleteRow(row);
      if (mapped) {
        suggestions.push(mapped);
      }
    }

    const deduped = this.dedupeByUrl(suggestions).slice(0, this.maxSuggestions);
    const hasExp = deduped.some((s) => s.type === 'experience');

    return {
      query,
      primary: deduped[0] || null,
      suggestions: deduped,
      experiencesLabel: hasExp ? 'Experiences' : undefined,
      guidance: deduped.length ? undefined : [...this.emptyGuidance],
    };
  }

  private mapAutocompleteRow(row: any): SmartSearchSuggestion | null {
    if (!row) {
      return null;
    }
    const type = String(row.type || '').toLowerCase();
    const label = String(row.label || row.title || row.name || '').trim();
    if (!label) {
      return null;
    }
    const slug = String(row.value || row.slug || row.params || '').trim();

    if (type === 'exp') {
      if (!slug) {
        return null;
      }
      const place = [row.city_name, row.state_name, row.country_name]
        .map((p) => (p ? String(p).trim() : ''))
        .filter(Boolean)
        .join(', ');
      let subtitle = place || 'Experience';
      const priceRaw = row?.all_in_one_price?.total_price ?? row?.price;
      const priceNum = priceRaw != null && priceRaw !== '' ? Number(priceRaw) : NaN;
      if (!Number.isNaN(priceNum) && priceNum > 0) {
        subtitle = `From $${priceNum.toFixed(0)}${place ? ' · ' + place : ''}`;
      } else if (row?.experience_type == 2) {
        subtitle = place ? `Any price · ${place}` : 'Any price';
      }
      return {
        id: `exp-${row.id || slug}`,
        type: 'experience',
        title: label,
        subtitle,
        url: `/experiences/${slug}`,
        confidence: 0.95,
        badge: 'Experience',
      };
    }

    if (type === 'location' || type === 'location-list') {
      const url = this.autocompleteLocationUrl(row, slug);
      if (!url) {
        return null;
      }
      const stateHint =
        row.state_name && String(row.state_name).trim()
          ? `(${String(row.state_name).trim()})`
          : '';
      return {
        id: `location-${row.id || slug || label}`,
        type: 'location',
        title: stateHint ? `${label} ${stateHint}` : label,
        subtitle: 'Location',
        url,
        confidence: 0.9,
        badge: 'Location',
      };
    }

    if (type === 'category' || type === 'higher-level-category') {
      if (!slug) {
        return null;
      }
      return {
        id: `category-${row.id || slug}`,
        type: 'category',
        title: label,
        subtitle: 'Category',
        url: `/experience/${slug}`,
        confidence: 0.88,
        badge: 'Category',
      };
    }

    // Unknown row type — still surface as a shop title search if we have a label
    return {
      id: `other-${type || 'q'}-${label}`,
      type: 'page',
      title: label,
      subtitle: 'Search',
      url: `/shop?title=${encodeURIComponent(label)}`,
      confidence: 0.6,
      badge: 'Search',
    };
  }

  private autocompleteLocationUrl(row: any, slug: string): string | null {
    const locationSlug = slug || String(row?.slug || '').trim();
    if (!locationSlug) {
      return null;
    }

    const isList = String(row?.type || '').toLowerCase() === 'location-list';
    const countrySlug = isList
      ? row?.super_parent_location_slug
      : row?.country_slug;
    const stateSlug = isList
      ? row?.parent_location_slug
      : row?.state_slug;

    const empty = (v: any) => v == null || v === '';

    if (empty(countrySlug) && empty(stateSlug)) {
      return `/location-region/${locationSlug}`;
    }
    if (empty(countrySlug) && !empty(stateSlug)) {
      return `/location-region/${stateSlug}/${locationSlug}`;
    }
    if (!empty(countrySlug) && !empty(stateSlug)) {
      return `/location-region/${countrySlug}/${stateSlug}/${locationSlug}`;
    }
    return `/location-region/${locationSlug}`;
  }

  private finalizeResult(result: SmartSearchResult): SmartSearchResult {
    const suggestions = (result.suggestions || []).slice(0, this.maxSuggestions);
    const empty = suggestions.length === 0;
    return {
      ...result,
      suggestions,
      primary: suggestions[0] || null,
      guidance: empty ? [...this.emptyGuidance] : undefined,
      experiencesLabel: result.experiencesLabel,
    };
  }

  /**
   * Prefer the longest place-name phrase actually present in the query.
   * Prefer city/state over country when both match the same phrase.
   */
  private resolvePrimaryLocation(query: string): ScoredHit | null {
    const normalized = this.normalize(query);
    let best: ScoredHit | null = null;

    for (const item of this.catalog) {
      if (item.type !== 'location') {
        continue;
      }
      const phrases = [this.normalize(item.name), ...item.aliases]
        .filter((p) => p && p.length >= 2)
        .sort((a, b) => b.length - a.length);

      for (const phrase of phrases) {
        if (!this.containsPhrase(normalized, phrase)) {
          continue;
        }
        // More specific places (have a parent) beat countries for the same phrase
        const specificity = item.parentSlug ? 0.06 : 0;
        const score = 0.9 + Math.min(0.08, phrase.length / 80) + specificity;
        const betterPhrase =
          !best ||
          phrase.length > (best.matchedAlias?.length || 0) ||
          (phrase.length === (best.matchedAlias?.length || 0) && score > best.score);
        if (betterPhrase) {
          best = { item, score, matchedAlias: phrase };
        }
        break;
      }
    }

    return best;
  }

  private pickLocationForExperiences(
    query: string,
    _local?: SmartSearchResult
  ): ScoredHit | null {
    return this.resolvePrimaryLocation(query);
  }

  /** @deprecated use activityTokens — kept for any residual callers */
  private leftoverSearchTitle(query: string, loc: CatalogItem): string {
    return this.activityTokens(this.normalize(query), loc).join(' ');
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /** True when candidate is the same place or a natural sub-area (e.g. Florida Keys). */
  private isRelatedLocation(candidate: CatalogItem, primary: CatalogItem): boolean {
    if (candidate.slug === primary.slug) {
      return true;
    }
    if (candidate.parentSlug === primary.slug) {
      return true;
    }
    const primaryName = this.normalize(primary.name);
    const candidateName = this.normalize(candidate.name);
    const candidateSlug = this.normalize(candidate.slug.replace(/-/g, ' '));
    if (primaryName.length >= 4) {
      if (candidateName.includes(primaryName) || candidateSlug.includes(primaryName)) {
        return true;
      }
    }
    return false;
  }

  private isSuggestionForLocation(
    suggestion: SmartSearchSuggestion,
    loc: CatalogItem
  ): boolean {
    if (suggestion.type === 'experience') {
      return true;
    }
    const title = this.normalize(suggestion.title);
    const locName = this.normalize(loc.name);
    const locSlug = loc.slug;

    if (suggestion.type === 'location') {
      if (suggestion.url.endsWith(`/${locSlug}`) || suggestion.url.includes(`/${locSlug}/`)) {
        return true;
      }
      if (locName.length >= 4 && title.includes(locName)) {
        return true;
      }
      return false;
    }
    if (suggestion.type === 'location_category') {
      return locName.length >= 4 && title.includes(locName);
    }
    if (suggestion.type === 'category') {
      // Already relevance-filtered in searchLocal; keep when browsing a place
      return true;
    }
    return false;
  }

  private fetchExperiencesForLocation(
    loc: CatalogItem,
    titleFilter: string
  ): Observable<SmartSearchSuggestion[]> {
    const cacheKey = `v3|${loc.slug}|${titleFilter}`;
    const cached = this.experienceCache.get(cacheKey);
    if (cached && Date.now() - cached.at < this.experienceCacheTtlMs) {
      return of(cached.items);
    }

    const body = {
      page: 1,
      type: '',
      order: '',
      location_slug: loc.slug,
      search_id: '',
      category_id: 0,
      price_option: {},
      order_by: '',
      is_booking: 0,
      start_date: '',
      end_date: '',
      is_offset: 0,
      lat_long: '',
      view_on_map: false,
      latitude: '',
      longitude: '',
      at_home: 0,
      title: titleFilter || '',
    };

    return this.experienceService.getExperienceByLocation1(body, '').pipe(
      timeout(this.experienceApiTimeoutMs),
      map((res: any) => {
        const rows = this.extractExperienceRows(res);
        const items = rows
          .filter((exp) => this.experienceBelongsToLocation(exp, loc))
          .slice(0, this.maxExperiences)
          .map((exp) => this.mapExperienceSuggestion(exp, loc.name))
          .filter((s): s is SmartSearchSuggestion => !!s);
        this.experienceCache.set(cacheKey, { at: Date.now(), items });
        if (this.experienceCache.size > 40) {
          const oldest = this.experienceCache.keys().next().value;
          if (oldest) {
            this.experienceCache.delete(oldest);
          }
        }
        return items;
      }),
      catchError(() => of([] as SmartSearchSuggestion[]))
    );
  }

  /** Drop foreign / placeholder listings that don't match the searched place. */
  private experienceBelongsToLocation(exp: any, loc: CatalogItem): boolean {
    const blob = this.normalize(
      [
        exp?.city,
        exp?.state,
        exp?.state_name,
        exp?.state_code,
        exp?.country,
        exp?.country_name,
        exp?.location_name,
        exp?.location_slug,
        exp?.region,
        exp?.title,
        exp?.slug,
      ]
        .filter(Boolean)
        .join(' ')
    );

    if (!blob) {
      return false;
    }
    // Placeholder / at-home inventory
    if (
      blob.includes('your city') ||
      blob.includes('your state') ||
      this.containsPhrase(blob, 'at home') ||
      this.containsPhrase(blob, 'in home')
    ) {
      return false;
    }

    if (this.hasConflictingGeo(blob, loc)) {
      return false;
    }

    const hints = this.geoHintsForLocation(loc);
    if (hints.some((h) => h.length >= 2 && this.containsPhrase(blob, h))) {
      return true;
    }

    // Country-level searches: trust API scoping if no geo conflict
    const isCountryLevel = !loc.parentSlug || loc.parentSlug === loc.slug;
    if (isCountryLevel) {
      return true;
    }

    // State/city searches must positively match (FL, Miami, Orlando…)
    return false;
  }

  /** Reject Pattaya/Santander/etc. when the user asked for Florida/Mexico/US. */
  private hasConflictingGeo(blob: string, loc: CatalogItem): boolean {
    const target = this.normalize(loc.name + ' ' + loc.slug);
    const foreignMarkers = [
      'thailand', 'pattaya', 'pataya', 'bangkok', 'phuket', 'chiang mai',
      'spain', 'santander', 'madrid', 'barcelona', 'seville', 'valencia',
      'greece', 'rhodes', 'athens', 'santorini',
      'france', 'paris', 'italy', 'rome', 'venice',
      'japan', 'tokyo', 'bali', 'indonesia', 'dubai', 'uae',
      'australia', 'sydney', 'uk', 'london', 'england',
      'costa rica', 'curacao', 'india', 'delhi', 'taj mahal'
    ];

    // If searching Florida / US state, these are conflicts
    const isUsPlace =
      target.includes('florida') ||
      target.includes('united states') ||
      loc.countrySlug === 'united-states' ||
      loc.parentSlug === 'united-states';

    const isMexico = target.includes('mexico');

    for (const marker of foreignMarkers) {
      if (!this.containsPhrase(blob, marker)) {
        continue;
      }
      // Marker is fine if it's part of the target place itself
      if (target.includes(marker)) {
        continue;
      }
      if (isUsPlace || isMexico) {
        return true;
      }
      // For other places, conflict if marker country clearly differs from target
      if (!target.includes(marker.split(' ')[0])) {
        return true;
      }
    }
    return false;
  }

  private geoHintsForLocation(loc: CatalogItem): string[] {
    const hints = new Set<string>();
    hints.add(this.normalize(loc.name));
    hints.add(this.normalize(loc.slug.replace(/-/g, ' ')));
    for (const a of loc.aliases) {
      hints.add(this.normalize(a));
    }
    const slugHints = this.locationGeoHints[loc.slug];
    if (slugHints) {
      slugHints.forEach((h) => hints.add(this.normalize(h)));
    }
    // Parent country hints for cities/states
    if (loc.parentSlug && this.locationGeoHints[loc.parentSlug]) {
      // For a US state, don't require country-wide hints — state hints are enough.
      // But include parent slug name if useful for countries under continent.
    }
    if (loc.countrySlug && loc.countrySlug !== loc.slug) {
      hints.add(this.normalize(loc.countrySlug.replace(/-/g, ' ')));
    }
    return [...hints].filter(Boolean);
  }

  private extractExperienceRows(res: any): any[] {
    if (!res || res.response !== 'success') {
      return [];
    }
    const data = res.data;
    if (Array.isArray(data)) {
      return data;
    }
    if (Array.isArray(data?.data)) {
      return data.data;
    }
    if (Array.isArray(data?.experiences)) {
      return data.experiences;
    }
    if (Array.isArray(data?.events)) {
      return data.events;
    }
    return [];
  }

  private mapExperienceSuggestion(
    exp: any,
    locName: string
  ): SmartSearchSuggestion | null {
    const slug = exp?.slug;
    const title = exp?.title || exp?.name;
    if (!slug || !title) {
      return null;
    }
    const placeParts = [exp?.city, exp?.state_name || exp?.state, exp?.country_name || exp?.country]
      .map((p) => (p ? String(p).trim() : ''))
      .filter((p) => p && !/^your\s/i.test(p));
    const place = placeParts.length ? placeParts.join(', ') : locName;
    const priceRaw =
      exp?.discounted_price ??
      exp?.discount_price ??
      exp?.offer_price ??
      exp?.price ??
      exp?.min_price ??
      exp?.starting_price;
    const priceNum = priceRaw != null && priceRaw !== '' ? Number(priceRaw) : NaN;
    let subtitle = `In ${place}`;
    if (!Number.isNaN(priceNum) && priceNum > 0) {
      subtitle = `From $${priceNum.toFixed(0)} · ${subtitle}`;
    }

    return {
      id: `exp-${exp.id || slug}`,
      type: 'experience',
      title: String(title),
      subtitle,
      url: `/experiences/${slug}`,
      confidence: 0.72,
      badge: 'Experience',
    };
  }

  /** When intent is clearly one place, don't surface unrelated destinations. */
  private focusSuggestionsOnLocation(
    local: SmartSearchResult,
    loc: CatalogItem
  ): SmartSearchResult {
    const focused = local.suggestions.filter((s) =>
      this.isSuggestionForLocation(s, loc)
    );

    if (!focused.some((s) => s.type === 'location' && this.isSuggestionForLocation(s, loc))) {
      focused.unshift(this.toSuggestion(loc, 0.99));
    }

    // De-dupe location cards; keep primary first
    const primaryCard = this.toSuggestion(loc, 0.99);
    const rest = focused.filter(
      (s) => !(s.type === 'location' && (s.url === primaryCard.url || s.title === loc.name))
    );
    const relatedLocations = rest.filter((s) => s.type === 'location');
    const categories = rest.filter(
      (s) => s.type === 'category' || s.type === 'location_category'
    );
    const experiences = rest.filter((s) => s.type === 'experience');

    return {
      ...local,
      suggestions: this.dedupeByUrl([
        primaryCard,
        ...relatedLocations.slice(0, 2),
        ...categories.slice(0, 2),
        ...experiences,
      ]),
    };
  }

  private mergeExperiences(
    local: SmartSearchResult,
    loc: CatalogItem,
    experiences: SmartSearchSuggestion[]
  ): SmartSearchResult {
    const nonExp = local.suggestions.filter((s) => s.type !== 'experience');
    const browse =
      nonExp.find((s) => s.type === 'location' && this.isSuggestionForLocation(s, loc)) ||
      this.toSuggestion(loc, 0.99);

    const categories = nonExp.filter(
      (s) =>
        (s.type === 'category' || s.type === 'location_category') &&
        this.isSuggestionForLocation(s, loc)
    );
    const relatedLocs = nonExp.filter(
      (s) =>
        s.type === 'location' &&
        s.id !== browse.id &&
        s.url !== browse.url &&
        this.isSuggestionForLocation(s, loc)
    );

    // Never append unrelated "others" (that was surfacing Pattaya / Santander)
    const suggestions = this.dedupeByUrl([
      browse,
      ...relatedLocs.slice(0, 2),
      ...categories.slice(0, 2),
      ...experiences,
    ]);

    return {
      ...local,
      suggestions,
      experiencesLabel: experiences.length
        ? `Experiences in ${loc.name}`
        : undefined,
    };
  }

  private searchLocal(query: string): SmartSearchResult {
    const normalized = this.normalize(query);
    const primaryLoc = this.resolvePrimaryLocation(query);
    const activityTokens = this.activityTokens(normalized, primaryLoc?.item);
    const wantsExplicitTour = this.queryWantsExplicitTour(normalized);

    const suggestions: SmartSearchSuggestion[] = [];

    if (primaryLoc) {
      suggestions.push(this.toSuggestion(primaryLoc.item, 0.99));

      const related = this.catalog
        .filter(
          (i) =>
            i.type === 'location' &&
            i.slug !== primaryLoc.item.slug &&
            this.isRelatedLocation(i, primaryLoc.item)
        )
        .slice(0, 2);
      for (const rel of related) {
        suggestions.push(this.toSuggestion(rel, 0.86));
      }

      if (activityTokens.length) {
        const cats = this.matchCategories(activityTokens, wantsExplicitTour, normalized);
        for (const hit of cats.slice(0, 3)) {
          suggestions.push(
            this.toCategorySuggestion(hit.item, hit.score, primaryLoc.item)
          );
        }
      } else if (wantsExplicitTour) {
        const tourCat = this.findTourishCategory();
        if (tourCat) {
          suggestions.unshift(
            this.comboSuggestion(primaryLoc.item, tourCat, 0.95, 0.7)
          );
        }
      }
    } else {
      // Activity-only query e.g. "helicopter rides" — no random locations
      const cats = this.matchCategories(activityTokens, wantsExplicitTour, normalized);
      for (const hit of cats.slice(0, 4)) {
        suggestions.push(this.toCategorySuggestion(hit.item, hit.score));
      }

      // Only add a location if its name literally appears in the query (already handled by primaryLoc).
      // Do not fuzzy-match locations against activity words.
    }

    const deduped = this.dedupeByUrl(suggestions)
      .filter((s) => s.confidence >= 0.55)
      .slice(0, 8);

    const guarded = primaryLoc
      ? deduped.filter(
          (s) =>
            s.type === 'experience' ||
            this.isSuggestionForLocation(s, primaryLoc.item) ||
            (s.type === 'category' && this.categoryMatchesActivity(s.title, activityTokens)) ||
            s.type === 'location_category'
        )
      : deduped.filter((s) => s.type !== 'location');

    return {
      query,
      primary: guarded[0] || null,
      suggestions: guarded,
    };
  }

  /**
   * Core activity words only — strips place names and generic "rides/tours/experiences".
   * "helicopter rides" → ["helicopter"]
   * "experiences in florida" → []
   * "city tour in new york" → ["city"] when tour is explicit… handled separately
   */
  private activityTokens(normalizedQuery: string, loc?: CatalogItem): string[] {
    let q = normalizedQuery;
    if (loc) {
      const phrases = [this.normalize(loc.name), ...loc.aliases].sort(
        (a, b) => b.length - a.length
      );
      for (const p of phrases) {
        if (p && this.containsPhrase(q, p)) {
          q = q.replace(new RegExp(`\\b${this.escapeRegExp(p)}\\b`, 'g'), ' ');
          break;
        }
      }
    }
    return q
      .split(/\s+/)
      .map((t) => t.trim())
      .filter(
        (t) =>
          t.length >= 3 &&
          !this.stopWords.has(t) &&
          !this.noiseTitleWords.has(t)
      );
  }

  /** Only true for clear tour intent — not every "ride". */
  private queryWantsExplicitTour(normalizedQuery: string): boolean {
    const explicit = [
      'city tour',
      'city tours',
      'sightseeing',
      'guided tour',
      'guided tours',
      'walking tour',
      'walking tours',
    ];
    return explicit.some((k) => this.containsPhrase(normalizedQuery, k));
  }

  private matchCategories(
    activityTokens: string[],
    wantsExplicitTour: boolean,
    fullQuery: string
  ): ScoredHit[] {
    if (!activityTokens.length && !wantsExplicitTour) {
      return [];
    }

    const scored: ScoredHit[] = [];
    for (const item of this.catalog) {
      if (item.type !== 'category') {
        continue;
      }
      // Skip "Helicopters in Cârțișoara" unless that place is in the query
      if (this.isPlaceSpecificCategory(item, fullQuery)) {
        continue;
      }

      const hit = this.scoreCategoryAgainstActivity(item, activityTokens);
      if (hit.score >= 0.72) {
        scored.push(hit);
      } else if (wantsExplicitTour && this.isTourish(item) && !activityTokens.length) {
        scored.push({ item, score: 0.7, matchedAlias: 'tour' });
      }
    }

    return scored.sort((a, b) => b.score - a.score);
  }

  /** Categories named like "X in {Place}" when Place is not in the query. */
  private isPlaceSpecificCategory(item: CatalogItem, fullQuery: string): boolean {
    const name = this.normalize(item.name);
    const m = name.match(/^(.+?)\s+in\s+(.+)$/);
    if (!m) {
      return false;
    }
    const placePart = (m[2] || '').trim();
    if (placePart.length < 3) {
      return false;
    }
    return !this.containsPhrase(fullQuery, placePart);
  }

  private scoreCategoryAgainstActivity(
    item: CatalogItem,
    activityTokens: string[]
  ): ScoredHit {
    if (!activityTokens.length) {
      return { item, score: 0 };
    }

    const name = this.normalize(item.name);
    const slug = this.normalize(item.slug.replace(/-/g, ' '));
    const haystacks = [name, slug, ...item.aliases].filter(Boolean);

    let best = 0;
    let matchedAlias = '';

    for (const hay of haystacks) {
      // Full activity phrase inside category name
      const activityPhrase = activityTokens.join(' ');
      if (activityPhrase.length >= 3 && this.containsPhrase(hay, activityPhrase)) {
        best = Math.max(best, 0.98);
        matchedAlias = hay;
        continue;
      }
      if (activityPhrase.length >= 3 && this.containsPhrase(activityPhrase, hay) && hay.length >= 4) {
        best = Math.max(best, 0.9);
        matchedAlias = hay;
        continue;
      }

      const hayStems = hay
        .split(/\s+/)
        .filter((t) => t.length >= 3 && !this.noiseTitleWords.has(t))
        .map((t) => this.stem(t));

      if (!hayStems.length) {
        continue;
      }

      let matched = 0;
      for (const at of activityTokens) {
        const as = this.stem(at);
        if (hayStems.some((hs) => hs === as || hs.startsWith(as) || as.startsWith(hs))) {
          // Require stem length >= 4 for prefix (heli↔helicopter) but exact stem OK at 3
          const hitStem = hayStems.find((hs) => hs === as || hs.startsWith(as) || as.startsWith(hs))!;
          if (as === hitStem || Math.min(as.length, hitStem.length) >= 4) {
            matched++;
          }
        }
      }

      if (matched === 0) {
        continue;
      }
      // All activity tokens should match for multi-word activities; for one token, full credit
      const coverage = matched / activityTokens.length;
      if (coverage < 1 && activityTokens.length > 1) {
        continue;
      }
      const score = 0.75 + 0.2 * coverage;
      if (score > best) {
        best = score;
        matchedAlias = hay;
      }
    }

    return { item, score: Math.min(1, best), matchedAlias };
  }

  private categoryMatchesActivity(title: string, activityTokens: string[]): boolean {
    if (!activityTokens.length) {
      return true;
    }
    const stems = this.normalize(title)
      .split(/\s+/)
      .map((t) => this.stem(t));
    return activityTokens.every((at) => {
      const as = this.stem(at);
      return stems.some(
        (hs) => hs === as || (Math.min(hs.length, as.length) >= 4 && (hs.startsWith(as) || as.startsWith(hs)))
      );
    });
  }

  /** Very small stemmer: helicopters→helicopter, rides→ride, balloons→balloon */
  private stem(word: string): string {
    let w = this.normalize(word);
    if (w.length <= 3) {
      return w;
    }
    if (w.endsWith('ies') && w.length > 5) {
      return w.slice(0, -3) + 'y';
    }
    if (w.endsWith('ses') || w.endsWith('zes') || w.endsWith('xes')) {
      return w.slice(0, -2);
    }
    if (w.endsWith('oes') && w.length > 4) {
      return w.slice(0, -2);
    }
    if (w.endsWith('es') && w.length > 4) {
      return w.slice(0, -2);
    }
    if (w.endsWith('s') && !w.endsWith('ss') && w.length > 3) {
      return w.slice(0, -1);
    }
    return w;
  }

  private searchViaAanya(query: string): Observable<SmartSearchResult> {
    void this.aanyaSmartSearchPath;
    const local = this.searchLocal(query);
    return of(this.finalizeResult(local)).pipe(delay(this.searchUxDelayMs));
  }

  private flattenLocationTree(
    items: CatalogItem[],
    loc: any,
    parentSlug?: string,
    countrySlug?: string
  ): void {
    if (!loc) {
      return;
    }

    if (loc.slug && loc.name) {
      this.pushLocation(items, loc, parentSlug, countrySlug || loc.slug);
    }

    const country = countrySlug || loc.slug;

    if (Array.isArray(loc.child_locations)) {
      for (const child of loc.child_locations) {
        this.flattenLocationTree(items, child, loc.slug, country);
      }
    }

    if (Array.isArray(loc.divisions)) {
      for (const division of loc.divisions) {
        const cities = division?.division_data;
        if (!Array.isArray(cities)) {
          continue;
        }
        for (const city of cities) {
          this.flattenLocationTree(items, city, country, country);
        }
      }
    }
  }

  private pushLocation(
    items: CatalogItem[],
    loc: any,
    parentSlug?: string,
    countrySlug?: string
  ): void {
    if (!loc?.slug || !loc?.name) {
      return;
    }
    const slug = String(loc.slug);
    const id = `location-${parentSlug || 'root'}-${slug}`;
    if (items.some((i) => i.id === id)) {
      return;
    }
    const name = String(loc.name);
    const tagline =
      (loc.h1_tag && String(loc.h1_tag).trim()) ||
      `Explore experiences in ${name}`;

    items.push({
      id,
      type: 'location',
      name,
      slug,
      parentSlug: parentSlug || undefined,
      countrySlug: countrySlug || parentSlug || slug,
      aliases: this.buildAliases(name, slug),
      tagline,
    });
  }

  private pushCategory(items: CatalogItem[], cat: any): void {
    if (!cat?.slug || !cat?.name) {
      return;
    }
    const slug = String(cat.slug);
    const name = String(cat.name);
    const tagline =
      (cat.h1_tag && String(cat.h1_tag).trim()) ||
      `Browse ${name} experiences`;

    items.push({
      id: `category-${slug}`,
      type: 'category',
      name,
      slug,
      aliases: this.buildAliases(name, slug),
      defaultLocSlug: this.defaultLocationSlug,
      tagline,
    });
  }

  private buildAliases(name: string, slug: string): string[] {
    const base = [
      this.normalize(name),
      this.normalize(slug.replace(/-/g, ' ')),
    ];
    // Only apply boosts for this slug (or exact key contained as whole slug segment)
    const boosts = this.aliasBoosts[slug] || [];
    base.push(...boosts);
    for (const [key, values] of Object.entries(this.aliasBoosts)) {
      if (key === slug) {
        continue;
      }
      // Avoid polluting "Santander" with spa aliases via loose includes
      const slugNorm = this.normalize(slug.replace(/-/g, ' '));
      const keyNorm = this.normalize(key.replace(/-/g, ' '));
      if (
        slugNorm === keyNorm ||
        slugNorm.startsWith(keyNorm + ' ') ||
        slugNorm.endsWith(' ' + keyNorm)
      ) {
        base.push(...values);
      }
    }
    return [...new Set(base.map((a) => this.normalize(a)).filter((a) => a.length >= 2))];
  }

  private scoreCategories(
    normalizedQuery: string,
    tokens: string[],
    _wantsTour: boolean
  ): ScoredHit[] {
    // Legacy path — prefer matchCategories(); keep strict scoring only
    return this.matchCategories(
      tokens.filter((t) => !this.noiseTitleWords.has(t)),
      this.queryWantsExplicitTour(normalizedQuery),
      normalizedQuery
    );
  }

  private scoreType(
    type: CatalogItem['type'],
    normalizedQuery: string,
    tokens: string[]
  ): ScoredHit[] {
    if (type === 'category') {
      return this.matchCategories(
        tokens.filter((t) => !this.noiseTitleWords.has(t)),
        this.queryWantsExplicitTour(normalizedQuery),
        normalizedQuery
      );
    }
    // Locations: phrase match only (no fuzzy activity→place)
    const scored: ScoredHit[] = [];
    for (const item of this.catalog) {
      if (item.type !== type) {
        continue;
      }
      const phrases = [this.normalize(item.name), ...item.aliases];
      for (const phrase of phrases) {
        if (phrase.length >= 3 && this.containsPhrase(normalizedQuery, phrase)) {
          scored.push({
            item,
            score: 0.9 + Math.min(0.08, phrase.length / 80),
            matchedAlias: phrase,
          });
          break;
        }
      }
    }
    return scored.sort((a, b) => b.score - a.score);
  }

  private scoreItem(
    item: CatalogItem,
    normalizedQuery: string,
    tokens: string[]
  ): ScoredHit {
    if (item.type === 'category') {
      return this.scoreCategoryAgainstActivity(
        item,
        tokens.filter((t) => !this.noiseTitleWords.has(t))
      );
    }
    const phrases = [this.normalize(item.name), ...item.aliases];
    for (const phrase of phrases) {
      if (phrase.length >= 3 && this.containsPhrase(normalizedQuery, phrase)) {
        return { item, score: 0.92, matchedAlias: phrase };
      }
    }
    return { item, score: 0 };
  }

  private toSuggestion(
    item: CatalogItem,
    score: number,
    intent?: 'tour'
  ): SmartSearchSuggestion {
    if (item.type === 'location') {
      return {
        id: item.id,
        type: 'location',
        title: item.name,
        subtitle: this.oneLiner(item, intent),
        url: this.locationUrl(item),
        confidence: score,
        badge: 'Location',
      };
    }
    if (item.type === 'page') {
      return {
        id: item.id,
        type: 'page',
        title: item.name,
        subtitle: this.oneLiner(item),
        url: `/${item.slug}`,
        confidence: score,
        badge: 'Page',
      };
    }
    return this.toCategorySuggestion(item, score);
  }

  private toCategorySuggestion(
    item: CatalogItem,
    score: number,
    preferredLoc?: CatalogItem
  ): SmartSearchSuggestion {
    const locSlug =
      preferredLoc?.slug ||
      this.defaultLocationSlug ||
      item.defaultLocSlug ||
      'united-states';
    return {
      id: `category-${item.slug}-${locSlug}`,
      type: 'category',
      title: item.name,
      subtitle: this.oneLiner(item),
      url: `/experience/${item.slug}/${locSlug}`,
      confidence: score,
      badge: 'Category',
    };
  }

  private comboSuggestion(
    loc: CatalogItem,
    cat: CatalogItem,
    locScore: number,
    catScore: number
  ): SmartSearchSuggestion {
    return {
      id: `combo-${loc.slug}-${cat.slug}`,
      type: 'location_category',
      title: `${cat.name} in ${loc.name}`,
      subtitle:
        cat.tagline ||
        `Discover ${cat.name.toLowerCase()} experiences around ${loc.name}`,
      url: `/experience/${cat.slug}/${loc.slug}`,
      confidence: Math.min(1, (locScore + catScore) / 2 + 0.18),
      badge: 'Match',
    };
  }

  private locationUrl(item: CatalogItem): string {
    if (item.parentSlug && item.parentSlug !== item.slug) {
      return `/location-region/${item.parentSlug}/${item.slug}`;
    }
    return `/location-region/${item.slug}`;
  }

  private oneLiner(item: CatalogItem, intent?: 'tour'): string {
    if (intent === 'tour' && item.type === 'location') {
      return `Find city tours and sightseeing experiences in ${item.name}`;
    }
    if (item.tagline) {
      return item.tagline;
    }
    if (item.type === 'location') {
      return `Explore experiences in ${item.name}`;
    }
    if (item.type === 'category') {
      return `Browse ${item.name} experiences`;
    }
    return `Go to ${item.name}`;
  }

  private findTourishCategory(): CatalogItem | null {
    const tours = this.catalog.filter(
      (i) => i.type === 'category' && this.isTourish(i)
    );
    return tours[0] || null;
  }

  private isTourish(item: CatalogItem): boolean {
    const blob = `${item.name} ${item.slug} ${item.aliases.join(' ')}`.toLowerCase();
    return /tour|sightsee|city.?tour|walking.?tour|guided/.test(blob);
  }

  private queryWantsTour(normalizedQuery: string): boolean {
    return this.queryWantsExplicitTour(normalizedQuery);
  }

  private containsPhrase(haystack: string, phrase: string): boolean {
    if (!phrase) {
      return false;
    }
    if (haystack === phrase) {
      return true;
    }
    return ` ${haystack} `.includes(` ${phrase} `);
  }

  private meaningfulTokens(normalized: string): string[] {
    return normalized
      .split(/\s+/)
      .filter(
        (t) =>
          t.length >= 2 &&
          !this.stopWords.has(t) &&
          !this.noiseTitleWords.has(t)
      );
  }

  private dedupeByUrl(list: SmartSearchSuggestion[]): SmartSearchSuggestion[] {
    const seen = new Set<string>();
    const out: SmartSearchSuggestion[] = [];
    for (const item of list) {
      const key = `${item.type}|${item.url}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      out.push(item);
    }
    return out;
  }

  private normalize(value: string): string {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, ' ')
      .replace(/-/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
}
